import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'react-toastify';

interface Cerca { latRef: number; lngRef: number; raioKm: number; raioBloqueioKm?: number; }
interface Props { value?: Cerca; onChange: (v: Cerca) => void; modoDuplo?: boolean; }
interface Sugestao { titulo: string; detalhe: string; lat: number; lng: number; }

const CENTRO_PADRAO = { lat: -15.78, lng: -47.93 }; // Brasil (visão ampla)
const RAIO_MIN = 0.5;
const RAIO_MAX = 300;
const RAIOS_RAPIDOS = [1, 5, 10, 25, 50, 100];

// Pino SVG embutido (sem depender de CDN para os ícones do Leaflet)
const PINO = L.divIcon({
  className: '',
  html: `<svg width="34" height="46" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 1C8.2 1 1 8.1 1 16.8 1 28.6 17 45 17 45s16-16.4 16-28.2C33 8.1 25.8 1 17 1z"
      fill="#1F467E" stroke="#fff" stroke-width="2"/>
    <circle cx="17" cy="16.5" r="6" fill="#fff"/>
  </svg>`,
  iconSize: [34, 46],
  iconAnchor: [17, 45],
});

// Slider em escala logarítmica: posições 0..100 → RAIO_MIN..RAIO_MAX km
const posParaKm = (t: number) => RAIO_MIN * Math.pow(RAIO_MAX / RAIO_MIN, t / 100);
const kmParaPos = (km: number) =>
  Math.round(100 * (Math.log(Math.max(km, RAIO_MIN) / RAIO_MIN) / Math.log(RAIO_MAX / RAIO_MIN)));
const arredondarKm = (km: number) => (km < 10 ? Math.round(km * 10) / 10 : Math.round(km));
const formatarKm = (km: number) => (km < 10 ? km.toFixed(1).replace('.', ',').replace(',0', '') : String(Math.round(km)));

const montarTituloPhoton = (p: any): { titulo: string; detalhe: string } => {
  const rua = [p.street || p.name, p.housenumber].filter(Boolean).join(', ');
  const titulo = p.name && p.street && p.name !== p.street ? p.name : rua || p.name || '';
  const detalhe = [p.street && p.name !== p.street ? rua : p.district, p.city, p.state, p.postcode]
    .filter((x, i, arr) => Boolean(x) && arr.indexOf(x) === i)
    .join(' · ');
  return { titulo: titulo || detalhe, detalhe: titulo ? detalhe : '' };
};

const CliqueNoMapa: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
};

const MapaCercaPicker: React.FC<Props> = ({ value, onChange, modoDuplo = false }) => {
  const [cerca, setCerca] = useState<Cerca>(() => {
    const base: Cerca = value && (value.latRef || value.lngRef)
      ? { ...value }
      : { latRef: 0, lngRef: 0, raioKm: value?.raioKm || 5 };
    if (modoDuplo && !(base.raioBloqueioKm && base.raioBloqueioKm > 0)) {
      base.raioBloqueioKm = value?.raioBloqueioKm || Math.min(RAIO_MAX, Math.max((base.raioKm || 5) * 3, (base.raioKm || 5) + 1));
    }
    return base;
  });
  const [busca, setBusca] = useState('');
  const [sugestoes, setSugestoes] = useState<Sugestao[]>([]);
  const [aberto, setAberto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [localizando, setLocalizando] = useState(false);
  const [endereco, setEndereco] = useState('');

  const mapRef = useRef<L.Map | null>(null);
  const cercaRef = useRef(cerca);
  cercaRef.current = cerca;

  const temPonto = cerca.latRef !== 0 || cerca.lngRef !== 0;

  const enquadrarCerca = useCallback((c: Cerca) => {
    if (!mapRef.current || (!c.latRef && !c.lngRef)) return;
    const raioEnquadrar = modoDuplo ? Math.max(c.raioKm, c.raioBloqueioKm || 0) : c.raioKm;
    const bounds = L.latLng(c.latRef, c.lngRef).toBounds(raioEnquadrar * 2 * 1000);
    mapRef.current.fitBounds(bounds, { padding: [28, 28] });
  }, [modoDuplo]);

  const atualizar = useCallback((patch: Partial<Cerca>, enquadrar = false) => {
    const nova = { ...cercaRef.current, ...patch };
    setCerca(nova);
    onChange(nova);
    if (enquadrar) enquadrarCerca(nova);
  }, [onChange, enquadrarCerca]);

  // ── Busca: CEP via BrasilAPI, endereço via Photon, Nominatim como fallback ──
  const buscarPorCep = async (cep: string): Promise<Sugestao[]> => {
    const resp = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
    if (!resp.ok) return [];
    const d = await resp.json();
    const rotulo = {
      titulo: [d.street, d.neighborhood].filter(Boolean).join(', ') || `CEP ${cep}`,
      detalhe: [d.city, d.state, `CEP ${cep}`].filter(Boolean).join(' · '),
    };
    const lat = parseFloat(d?.location?.coordinates?.latitude);
    const lng = parseFloat(d?.location?.coordinates?.longitude);
    if (!isNaN(lat) && !isNaN(lng)) return [{ ...rotulo, lat, lng }];
    // CEP sem coordenadas na BrasilAPI: resolve o endereço textual no Photon
    const q = [d.street, d.city, d.state].filter(Boolean).join(' ');
    return q ? (await buscarPhoton(q)).map((s) => ({ ...s, ...rotulo })) : [];
  };

  const buscarPhoton = async (q: string): Promise<Sugestao[]> => {
    const centro = mapRef.current?.getCenter();
    const bias = centro ? `&lat=${centro.lat}&lon=${centro.lng}` : '';
    const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&bbox=-74.1,-34.0,-28.6,5.3${bias}`;
    const resp = await fetch(url);
    if (!resp.ok) return [];
    const data = await resp.json();
    return (data?.features || [])
      .filter((f: any) => f?.properties?.countrycode === 'BR' && f?.geometry?.coordinates)
      .map((f: any) => ({
        ...montarTituloPhoton(f.properties),
        lat: f.geometry.coordinates[1],
        lng: f.geometry.coordinates[0],
      }));
  };

  const buscarNominatim = async (q: string): Promise<Sugestao[]> => {
    const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=br&limit=5&q=${encodeURIComponent(q)}`;
    const resp = await fetch(url, { headers: { 'Accept-Language': 'pt-BR' } });
    if (!resp.ok) return [];
    const data = await resp.json();
    return (Array.isArray(data) ? data : []).map((d: any) => {
      const partes = String(d.display_name || '').split(', ');
      return {
        titulo: partes.slice(0, 2).join(', '),
        detalhe: partes.slice(2, 5).join(' · '),
        lat: parseFloat(d.lat),
        lng: parseFloat(d.lon),
      };
    });
  };

  useEffect(() => {
    const texto = busca.trim();
    if (texto.length < 3) { setSugestoes([]); setAberto(false); return; }
    const t = setTimeout(async () => {
      setBuscando(true);
      setAberto(true);
      try {
        const digitos = texto.replace(/\D/g, '');
        const ehCep = /^\d{5}-?\d{3}$/.test(texto) || digitos.length === 8;
        let resultados = ehCep ? await buscarPorCep(digitos) : await buscarPhoton(texto);
        if (!resultados.length && !ehCep) resultados = await buscarNominatim(texto);
        setSugestoes(resultados);
      } catch {
        setSugestoes([]);
      } finally {
        setBuscando(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  const escolher = (s: Sugestao) => {
    setBusca(s.titulo);
    setSugestoes([]);
    setAberto(false);
    atualizar({ latRef: s.lat, lngRef: s.lng }, true);
  };

  // ── Endereço do ponto marcado (geocoding reverso: Photon → fallback Nominatim) ──
  useEffect(() => {
    if (!temPonto) { setEndereco(''); return; }
    const { latRef, lngRef } = cerca;
    const t = setTimeout(async () => {
      try {
        const resp = await fetch(`https://photon.komoot.io/reverse?lat=${latRef}&lon=${lngRef}&limit=1`);
        const data = resp.ok ? await resp.json() : null;
        const props = data?.features?.[0]?.properties;
        if (props) {
          const r = montarTituloPhoton(props);
          setEndereco([r.titulo, r.detalhe].filter(Boolean).join(' · '));
          return;
        }
        const resp2 = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latRef}&lon=${lngRef}`,
          { headers: { 'Accept-Language': 'pt-BR' } }
        );
        const d2 = resp2.ok ? await resp2.json() : null;
        setEndereco(d2?.display_name ? String(d2.display_name).split(', ').slice(0, 5).join(', ') : '');
      } catch {
        setEndereco('');
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cerca.latRef, cerca.lngRef, temPonto]);

  const usarMinhaLocalizacao = useCallback(() => {
    if (!navigator.geolocation) {
      toast.warning('Este navegador não permite acessar a localização.');
      return;
    }
    setLocalizando(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocalizando(false);
        atualizar({ latRef: pos.coords.latitude, lngRef: pos.coords.longitude }, true);
        if (pos.coords.accuracy > 1500) {
          toast.warning('Localização aproximada (obtida pela rede, sem GPS). Confira e ajuste o ponto no mapa.');
        }
      },
      () => {
        setLocalizando(false);
        toast.warning('Não foi possível obter sua localização. Busque o endereço ou clique no mapa.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [atualizar]);

  const aoMudarRaio = (km: number, enquadrar: boolean) => {
    const limitado = Math.min(RAIO_MAX, Math.max(RAIO_MIN, km));
    atualizar({ raioKm: arredondarKm(limitado) }, enquadrar && temPonto);
  };

  const aoMudarRaioBloqueio = (km: number, enquadrar: boolean) => {
    const limitado = Math.min(RAIO_MAX, Math.max(RAIO_MIN, km));
    atualizar({ raioBloqueioKm: arredondarKm(limitado) }, enquadrar && temPonto);
  };

  const controleRaio = (
    label: string,
    valorKm: number,
    onChange: (km: number, enquadrar: boolean) => void,
    cor: string
  ) => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border border-gray-200 rounded-md px-3 py-2 bg-gray-50">
      <span className="text-sm font-medium text-gray-700 whitespace-nowrap" style={{ minWidth: 108 }}>{label}</span>
      <input
        type="range"
        min={0}
        max={100}
        value={kmParaPos(valorKm)}
        onChange={(e) => onChange(posParaKm(Number(e.target.value)), false)}
        onMouseUp={() => temPonto && enquadrarCerca(cercaRef.current)}
        onTouchEnd={() => temPonto && enquadrarCerca(cercaRef.current)}
        className="flex-1 min-w-[120px]"
        style={{ accentColor: cor }}
      />
      <div className="flex items-center gap-1">
        <input
          type="number"
          min={RAIO_MIN}
          max={RAIO_MAX}
          step={0.1}
          value={valorKm}
          onChange={(e) => onChange(Number(e.target.value) || RAIO_MIN, true)}
          className="border border-gray-300 rounded px-2 py-1 text-sm w-20 text-right"
        />
        <span className="text-sm text-gray-600">km</span>
      </div>
      <div className="flex items-center gap-1">
        {RAIOS_RAPIDOS.map((km) => (
          <button
            key={km}
            type="button"
            onClick={() => onChange(km, true)}
            className="px-2 py-0.5 rounded-full text-xs border"
            style={
              arredondarKm(valorKm) === km
                ? { background: cor, color: '#fff', borderColor: cor }
                : { background: '#fff', color: '#4b5563', borderColor: '#d1d5db' }
            }
          >
            {formatarKm(km)}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onFocus={() => { if (sugestoes.length) setAberto(true); }}
            onBlur={() => setTimeout(() => setAberto(false), 150)}
            onKeyDown={(e) => { if (e.key === 'Enter' && sugestoes.length) { e.preventDefault(); escolher(sugestoes[0]); } }}
            placeholder="Busque por endereço, cidade ou CEP"
            className="border border-gray-300 rounded px-3 py-2 text-sm w-full"
          />
          {aberto && (
            <ul className="absolute z-[1100] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
              {buscando && (
                <li className="px-3 py-2 text-sm text-gray-400">Buscando…</li>
              )}
              {!buscando && sugestoes.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-400">Nenhum endereço encontrado. Tente rua + cidade, ou o CEP.</li>
              )}
              {sugestoes.map((s, i) => (
                <li
                  key={i}
                  onMouseDown={() => escolher(s)}
                  className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                >
                  <div className="text-sm text-gray-800">{s.titulo}</div>
                  {s.detalhe && <div className="text-xs text-gray-400">{s.detalhe}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={usarMinhaLocalizacao}
          disabled={localizando}
          title="Usa a localização do navegador (em computador sem GPS pode ser imprecisa)"
          className="flex items-center gap-1.5 border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="7" />
            <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
            <line x1="12" y1="1" x2="12" y2="5" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="1" y1="12" x2="5" y2="12" />
            <line x1="19" y1="12" x2="23" y2="12" />
          </svg>
          {localizando ? 'Localizando…' : 'Minha localização'}
        </button>
      </div>

      <div className="relative" style={{ height: 300, borderRadius: 8, overflow: 'hidden' }}>
        <MapContainer
          center={temPonto ? [cerca.latRef, cerca.lngRef] : [CENTRO_PADRAO.lat, CENTRO_PADRAO.lng]}
          zoom={temPonto ? 13 : 4}
          style={{ height: '100%', width: '100%' }}
          whenCreated={(m) => { mapRef.current = m; if (temPonto) enquadrarCerca(cercaRef.current); }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <CliqueNoMapa onPick={(lat, lng) => atualizar({ latRef: lat, lngRef: lng })} />
          {temPonto && (
            <>
              <Marker
                position={[cerca.latRef, cerca.lngRef]}
                icon={PINO}
                draggable
                eventHandlers={{
                  dragend: (e) => { const p = e.target.getLatLng(); atualizar({ latRef: p.lat, lngRef: p.lng }); },
                }}
              />
              {modoDuplo ? (
                <>
                  <Circle
                    center={[cerca.latRef, cerca.lngRef]}
                    radius={(cerca.raioBloqueioKm || 0) * 1000}
                    pathOptions={{ color: '#dc2626', weight: 2, fillOpacity: 0.05 }}
                  />
                  <Circle
                    center={[cerca.latRef, cerca.lngRef]}
                    radius={cerca.raioKm * 1000}
                    pathOptions={{ color: '#f59e0b', weight: 2, fillOpacity: 0.08 }}
                  />
                </>
              ) : (
                <Circle
                  center={[cerca.latRef, cerca.lngRef]}
                  radius={cerca.raioKm * 1000}
                  pathOptions={{ color: '#1F467E', weight: 2, fillOpacity: 0.08 }}
                />
              )}
            </>
          )}
        </MapContainer>

        {!temPonto && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-white/95 shadow rounded-full px-4 py-1.5 text-sm text-gray-600 pointer-events-none whitespace-nowrap">
            Busque um endereço ou clique no mapa para marcar o ponto
          </div>
        )}

        {temPonto && endereco && (
          <div
            className="absolute bottom-3 left-3 z-[1000] bg-white/95 shadow rounded-md px-3 py-1.5 text-xs text-gray-700 max-w-[75%] truncate"
            title={endereco}
          >
            {endereco}
          </div>
        )}
      </div>

      {modoDuplo ? (
        <>
          {controleRaio('Raio de alerta', cerca.raioKm, aoMudarRaio, '#f59e0b')}
          {controleRaio('Raio de bloqueio', cerca.raioBloqueioKm || 0, aoMudarRaioBloqueio, '#dc2626')}
          {(cerca.raioBloqueioKm || 0) < cerca.raioKm && (
            <span className="text-xs text-red-600">
              O raio de bloqueio deve ser maior ou igual ao raio de alerta.
            </span>
          )}
        </>
      ) : (
        controleRaio('Raio da cerca', cerca.raioKm, aoMudarRaio, '#1F467E')
      )}

      {temPonto && (
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {cerca.latRef.toFixed(5)}, {cerca.lngRef.toFixed(5)}
        </span>
      )}
    </div>
  );
};

export default MapaCercaPicker;
